/**
 * ProviderSuggestions — V101-1-9. Providers the workspace already uses
 * (systems, boundary, supplier, discovery) with no connector yet, strongest
 * first. Each row: provider name (opens ProviderDetail), "Used by N systems"
 * (or "Used here" on a system), source badges, admin-key marker, Connect +
 * Dismiss (owner/admin) or AskAdmin. Dismissed suggestions stay restorable
 * from "Dismissed (N)".
 *
 * Connect always goes through Setup's `?connect=<kind>`: on Setup that opens
 * the PostureConnections drawer in place; from a system it navigates there
 * with a way back.
 *
 * API: GET /api/w/:slug/providers/suggestions[?system_id=] and
 * …/suggestions/dismissed (any member); POST/DELETE …/suggestions/:kind/dismiss
 * (owner/admin — enforced server-side, `editable` only hides the controls).
 * Nothing to show → renders nothing.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, StatusGlyph } from '@aigovops/design-system';
import {
  dismissProviderSuggestion,
  listDismissedProviderSuggestions,
  listProviderSuggestions,
  restoreProviderSuggestion,
  type ProviderSuggestion,
} from '../lib/api';
import { queryKeys, useInvalidate } from '../lib/query-keys';
import { onboardingHref } from '../lib/onboarding-model';
import { AskAdmin } from './AskAdmin';
import { POSTURE_KIND_SET } from './PostureConnections';
import { ProviderDetail, channelLabel } from './ProviderDetail';
import './provider-suggestions.css';

const SOURCE_LABEL = { boundary: 'Boundary', supplier: 'Supplier', discovered: 'Discovered' } as const;

/** The row's usage line: "Used here" on a system, else "Used by N systems" (distinct systems). */
export function usageLabel(s: Pick<ProviderSuggestion, 'evidence' | 'used_here'>): string | null {
  if (s.used_here) return 'Used here';
  const n = s.evidence.find(e => e.source === 'systems')?.count ?? 0;
  return n > 0 ? `Used by ${n} ${n === 1 ? 'system' : 'systems'}` : null;
}

/** Neutral source badges (Boundary, Supplier, Discovered) plus "via Bedrock" for a channel-derived suggestion. */
export function evidenceChips(s: Pick<ProviderSuggestion, 'evidence' | 'via_channel'>): string[] {
  const chips: string[] = s.evidence.filter(e => e.count > 0 && e.source !== 'systems').map(e => SOURCE_LABEL[e.source as keyof typeof SOURCE_LABEL]);
  if (s.via_channel) chips.push(`via ${channelLabel(s.via_channel).replace(/^AWS /, '')}`);
  return chips;
}

/** Where Connect goes for a suggested kind (with a way back when coming from a system). */
export function connectHref(slug: string, kind: string, back?: { to: string; label: string }): string {
  if (!POSTURE_KIND_SET.has(kind)) return onboardingHref(slug, 'connect');
  const q = new URLSearchParams({ connect: kind });
  if (back) { q.set('back', back.to); q.set('back_label', back.label); }
  return `/w/${slug}/app/setup?${q.toString()}`;
}

export function ProviderSuggestions({ slug, editable, systemId, systemName, limit, quiet = false }: {
  /** V108-6-4: on Setup the page's one filled primary is the open setup step; suggestions stay secondary. */
  quiet?: boolean;
  slug: string; editable: boolean;
  /** Only providers used by this system (SystemDetail; server-filtered). */
  systemId?: string;
  systemName?: string;
  /** Cap the list; "See all" goes to Setup. */
  limit?: number;
}) {
  const query = useQuery({ queryKey: queryKeys.providerSuggestions(slug, systemId), queryFn: () => listProviderSuggestions(slug, systemId), enabled: !!slug });
  const dismissedQ = useQuery({ queryKey: queryKeys.dismissedProviderSuggestions(slug), queryFn: () => listDismissedProviderSuggestions(slug), enabled: !!slug && !systemId && editable });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState<ProviderSuggestion | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [live, setLive] = useState('');
  const undoBtn = useRef<HTMLButtonElement | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const restoreFocus = useRef<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const say = (m: string) => { setLive(''); window.setTimeout(() => setLive(m), 50); };

  const rows = query.data?.suggestions ?? [];
  // The just-dismissed kind is offered by its Restore bar, not twice.
  const dismissed = (dismissedQ.data?.dismissed ?? []).filter(d => d.connector_kind !== hidden?.connector_kind);

  // Focus follows the action: Dismiss → Undo; Undo / Restore → the restored row (else the heading).
  useEffect(() => { if (hidden) undoBtn.current?.focus(); }, [hidden]);
  useEffect(() => {
    const kind = restoreFocus.current;
    if (!kind) return;
    const row = list.current?.querySelector<HTMLElement>(`[data-kind="${kind}"] .v101-provider-link`);
    const settle = () => { restoreFocus.current = null; setRestoring(null); setHidden(h => (h?.connector_kind === kind ? null : h)); };
    // The Restore bar stays mounted ("Restoring…") until focus has somewhere to land.
    if (row) { row.focus(); settle(); }
    else if (!query.isFetching) { heading.current?.focus(); settle(); }
  }, [rows, query.isFetching]);

  // SystemDetail: hold the card's space while loading so the tabs below don't jump.
  if (systemId && query.isLoading) return <div className="v101-suggestions-skeleton" aria-hidden="true" />;

  const cap = limit;
  const shown = cap ? rows.slice(0, cap) : rows;
  if (!shown.length && !hidden && !(editable && !systemId && dismissed.length)) return null;

  const back = systemId ? { to: `/w/${slug}/app/systems/${encodeURIComponent(systemId)}`, label: systemName ?? 'the system' } : undefined;
  const connect = (s: ProviderSuggestion) => navigate(connectHref(slug, s.connector_kind, back));
  const dismiss = async (s: ProviderSuggestion) => {
    if (busy) return;
    setBusy(s.connector_kind); setError(null);
    try { await dismissProviderSuggestion(slug, s.connector_kind); setHidden(s); say(`${s.provider_name} hidden.`); invalidate('provider-suggestion:changed', slug); }
    catch (e) { setError(e instanceof Error ? e.message : 'That did not work. Please retry.'); }
    finally { setBusy(null); }
  };
  const restore = async (kind: string, name: string) => {
    if (busy) return;
    setBusy(`restore:${kind}`); setError(null);
    try {
      await restoreProviderSuggestion(slug, kind);
      say(`${name} restored.`);
      setRestoring(kind);
      restoreFocus.current = kind;
      invalidate('provider-suggestion:changed', slug);
    } catch (e) { setRestoring(null); setError(e instanceof Error ? e.message : 'That did not work. Please retry.'); }
    finally { setBusy(null); }
  };
  const titleId = `suggestions-title${systemId ? '-sys' : ''}`;

  return <Card className="v101-suggestions-card"><section id={systemId ? undefined : 'suggested-connections'} className={`v75-panel v101-suggestions${systemId ? ' compact' : ''}`} aria-labelledby={titleId}>
    <div className="v75-page-heading">
      <h2 id={titleId} ref={heading} tabIndex={-1}>Suggested connections</h2>
      {cap && rows.length > cap && <Link to={`/w/${slug}/app/setup#suggested-connections`}>See all {rows.length}</Link>}
    </div>
    <div role="status" aria-live="polite" className="sr-only">{live}</div>
    {error && <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert>}
    {hidden && <div className="v101-hidden"><span>{hidden.provider_name} hidden.</span>
      <Button ref={undoBtn} variant="ghost" aria-disabled={!!busy || !!restoring || undefined} onClick={() => { if (!restoring) void restore(hidden.connector_kind, hidden.provider_name); }}>
        {busy === `restore:${hidden.connector_kind}` || restoring === hidden.connector_kind ? 'Restoring…' : 'Restore'}<span className="sr-only">{` ${hidden.provider_name}`}</span>
      </Button></div>}
    {shown.length > 0 && <ul className="v101-suggestion-list" ref={list}>
      {shown.map((s, i) => {
        const usage = usageLabel(s);
        const chips = evidenceChips(s);
        return <li key={s.connector_kind} className="v101-suggestion" data-kind={s.connector_kind}>
          <div className="v101-suggestion-main">
            <div className="v101-suggestion-name">
              <button type="button" className="v101-provider-link" aria-haspopup="dialog" onClick={() => setOpen(s.provider_key)}>{s.provider_name}</button>
              {s.privilege && <Badge className="v75-status warning"><StatusGlyph kind="warning" /> Uses an admin key</Badge>}
            </div>
            <div className="v101-evidence">
              {usage && <span className="v101-usage">{usage}</span>}
              {chips.length > 0 && <ul className="v101-chips" aria-label={`Also seen in, for ${s.provider_name}`}>
                {chips.map(c => <li key={c}><Badge>{c}</Badge></li>)}
              </ul>}
            </div>
          </div>
          {editable && <div className="v75-actions v101-suggestion-actions">
            <Button variant={i === 0 && !systemId && !quiet ? 'primary' : 'secondary'} onClick={() => connect(s)}>{`Connect ${s.label}`}</Button>
            <Button variant="ghost" aria-disabled={!!busy || undefined} onClick={() => void dismiss(s)}>
              {busy === s.connector_kind ? 'Dismissing…' : systemId ? 'Dismiss everywhere' : 'Dismiss'}<span className="sr-only">{` ${s.provider_name}`}</span>
            </Button>
          </div>}
        </li>;
      })}
    </ul>}
    {!shown.length && !hidden && <p className="v75-muted">No new suggestions.</p>}
    {editable && !systemId && dismissed.length > 0 && <div className="v101-dismissed">
      <button type="button" className="v101-dismissed-toggle" aria-expanded={showDismissed} aria-controls="v101-dismissed-list" onClick={() => setShowDismissed(v => !v)}>
        Dismissed ({dismissed.length})
      </button>
      <ul id="v101-dismissed-list" className="v101-dismissed-list" hidden={!showDismissed}>
        {dismissed.map(d => <li key={d.connector_kind}>
          <span>{d.label}</span>
          <Button variant="ghost" aria-disabled={!!busy || undefined} onClick={() => void restore(d.connector_kind, d.label)}>
            {busy === `restore:${d.connector_kind}` || restoring === d.connector_kind ? 'Restoring…' : 'Restore'}<span className="sr-only">{` ${d.label}`}</span>
          </Button>
        </li>)}
      </ul>
    </div>}
    {!editable && <AskAdmin slug={slug} what="connects providers" subject="Suggested connections in GAC Pro" />}
    {open && <ProviderDetail slug={slug} providerKey={open} editable={editable} systemId={systemId}
      onClose={() => setOpen(null)} onConnect={kind => { setOpen(null); navigate(connectHref(slug, kind, back)); }} />}
  </section></Card>;
}
